"use client";

import { useTranslations } from "next-intl";

import GlowButton from "../../ui/GlowButton";

import ResponsiveCopy from "../../ui/ResponsiveCopy";

// Shared entrance: fade + slight rise, via the `rise-in` CSS animation in
// globals.css rather than framer-motion.
//
// This is the LCP block on the home page, and LCP does not count an element
// while it sits at opacity 0. Animating it in JS meant the clock ran until
// framer had hydrated — measured at 9.2s of render delay on a throttled phone.
// The CSS animation starts at the first paint, so the text counts as painted
// straight away. Delays stand in for the old stagger.
const STAGGER = 0.12;

// Shared by both halves of the headline's responsive pair, so the two can't
// drift apart.
const highlight = (chunks) => <span className="text-sky-blue">{chunks}</span>;

export default function HeroContent() {
  const t = useTranslations("home.hero");
  const hasFirstLine = Boolean(t.raw("line1") || t.raw("line1Mobile"));

  return (
    <div className="mx-auto flex max-w-[1224px] flex-col items-center gap-6 text-center lg:gap-4">
      {/* Headline */}
      <h1 className={`rise-in font-bold leading-[1.15] text-white th:leading-[1.35] ${hasFirstLine ? "text-[clamp(28px,6vw,48px)]" : "text-[max(1px,calc(clamp(2rem,4vw,48px)-8px))]"}`}>
        {/* `text-balance` evens the lines out when a locale's copy is long
            enough to wrap (Thai runs wider than English at the same size), so
            no line ends up with a single orphaned word. */}
        {hasFirstLine && <span className="block text-balance">
          <ResponsiveCopy
            desktop={t.rich("line1", { hl: highlight })}
            mobile={t.rich("line1Mobile", { hl: highlight })}
          />
        </span>}
        <span className={`${hasFirstLine ? "mt-1 " : ""}block text-balance`}>
          <ResponsiveCopy
            desktop={t.rich("line2", { hl: highlight })}
            mobile={t.rich("line2Mobile", { hl: highlight })}
          />
        </span>
      </h1>

      {/* Subtitle */}
      <p
        style={{ animationDelay: `${STAGGER}s` }}
        // A readable measure rather than the width of the headline above it:
        // at 1006px (and more so at the 1280 it was briefly given) a line of
        // 16px Thai ran past 120 characters.
        // A \n in the copy breaks the line here — ResponsiveCopy carries the
        // `pre-line`. Each side of the breakpoint has its own field, so a break
        // written for a wide line never has to survive a narrow one.
        className="rise-in max-w-3xl text-base leading-normal text-[#a1a1a6]"
      >
        <ResponsiveCopy desktop={t("subtitle")} mobile={t("subtitleMobile")} />
      </p>
      <div className="grid w-full max-w-[420px] grid-cols-2 items-stretch gap-3">
        <GlowButton href="/contact-us" className="w-full !px-3">{t("primaryCta")}</GlowButton>
        <GlowButton href="/portfolio" variant="ghost" className="w-full !px-3">{t("secondaryCta")}</GlowButton>
      </div>
      <p className="max-w-lg text-sm leading-relaxed text-white/70">{t("contactHint")}</p>
    </div>
  );
}
